import {diApi} from '@di-shared/api/di';
import {
  CreatedMaintenanceRecordRequest,
  MaintenanceNotificationResponse,
  MaintenanceNotificationWithRecordResponse,
} from '@di-shared/api/di/__generated';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {t} from '@di-shared/features/common/useInferableTranslation';
import useSestCodeToName from '@di-shared/features/diag/sest/useSestCodeToName';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {parseDateWithFormat} from '@shared/dateUtils';
import {WebviewUtils} from '@shared/webviewUtils';

function useUnreadNotification(vin: string) {
  const {convertSestCodeToName} = useSestCodeToName();
  const {openAlert} = useCcWebviewUtilsHelper();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  function isDisplayableNotificationType(type: MaintenanceNotificationResponse.type) {
    return [
      MaintenanceNotificationResponse.type.NEW_RECORD,
      MaintenanceNotificationResponse.type.KEEP_RECORD,
      MaintenanceNotificationResponse.type.COMPARISON,
    ].includes(type);
  }

  async function checkUnreadServiceCenterNotifications() {
    /**
     * ignore 에러났을때 의도적인 무시
     */
    try {
      const {data: unreadServiceCenterNotifications} = await diApi.maintenanceNotificationController.getUnreadServiceCenterNotifications({
        vin,
      });
      const initialNoRecordNotifications = unreadServiceCenterNotifications.flatMap(({notifications}) =>
        notifications.filter(({type}) => type === MaintenanceNotificationResponse.type.INITIAL_NO_RECORD)
      );
      const hasDisplayableNotifications = unreadServiceCenterNotifications.some(({notifications}) =>
        notifications.some(({type}) => isDisplayableNotificationType(type))
      );

      if (initialNoRecordNotifications.length > 0) {
        // openAlert는 버튼 선택 결과를 반환하지 않아, 확인 버튼 클릭 시점에만 읽음처리하려면 openCustomAlert를 직접 사용해야 한다.
        const selectedIndex = await WebviewUtils.openCustomAlert({
          title: t('T30630'),
          message: t('T30629'),
          buttons: [{text: t('T10114'), style: 'PRIMARY', actionType: 'DEFAULT'}],
        });

        if (selectedIndex === 0) {
          await Promise.all(
            initialNoRecordNotifications.map(({id, actionTaken}) =>
              diApi.maintenanceNotificationController.updateNotification2({
                vin,
                notificationId: id,
                action: actionTaken,
              })
            )
          );
        }
      }

      if (hasDisplayableNotifications) {
        return await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_DIAG_SEST_EXCHANGE_HISTORY_AUTO.getFullPath(), {
          vin,
        });
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function checkUnreadUserNotifications() {
    /**
     * ignore 에러났을때 의도적인 무시
     */
    try {
      const {data: unreadUserNotifications} = await diApi.maintenanceNotificationController.getUnreadUserNotifications({
        vin,
      });
      const newRecordNotification = unreadUserNotifications.filter(({type}) => type === MaintenanceNotificationResponse.type.NEW_RECORD);
      const keepRecordNotification = unreadUserNotifications.filter(({type}) => type === MaintenanceNotificationResponse.type.KEEP_RECORD);
      const comparisonNotification = unreadUserNotifications.filter(({type}) => type === MaintenanceNotificationResponse.type.COMPARISON);
      if (newRecordNotification.length > 0) {
        newRecordNotification.forEach(({id}) => {
          diApi.maintenanceNotificationController.updateNotification2({
            vin,
            notificationId: id,
            action: MaintenanceNotificationWithRecordResponse.actionTaken.RECORD_ADDED,
          });
        });
      }
      if (keepRecordNotification.length > 0) {
        const message = keepRecordNotification.reduce((acc, {newRecord: {sestCode, replaceDate}}) => {
          const parsedReplaceDate = parseDateWithFormat(replaceDate);
          return (
            acc +
            `\n- ${convertSestCodeToName(sestCode as CreatedMaintenanceRecordRequest.sestCode)} : ${parsedReplaceDate ? CldrDatetimeFormatter.format(parsedReplaceDate, 'yMMMd') : ''}`
          );
        }, t('T30336'));
        await openAlert({title: t('T30337'), message, okText: t('T10114')});
        keepRecordNotification.forEach(({id}) => {
          diApi.maintenanceNotificationController.updateNotification2({
            vin,
            notificationId: id,
            action: MaintenanceNotificationWithRecordResponse.actionTaken.PENDING_DECISION,
          });
        });
      }
      if (comparisonNotification.length > 0) {
        await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_DIAG_SEST_EXCHANGE_HISTORY_MANUAL.getFullPath(), {
          vin,
          comparisonNotification,
        });
      }
    } catch (e) {
      console.error(e);
    }
  }

  return {checkUnreadServiceCenterNotifications, checkUnreadUserNotifications};
}

export default useUnreadNotification;
